import { Organization, UserPreview } from '@main/access-control-types';
import { mkInput, mkType, type SchemaToType } from '@main/schema-utils';

export const ConsentSite = mkType({
  name: 'ConsentSite',
  comment: 'Sites (Domains) found through telemetry or added by users',
  fields: {
    id: {
      type: 'id',
      comment: 'The ID of this site',
      modelName: 'consentSite',
    },
    lastDiscoveredAt: {
      type: 'Date',
      comment:
        'Indicates when telemetry data has been encountered from this site, and if so how old it is',
    },
    site: {
      type: 'string',
      comment: 'The TLD for the site',
    },
    host: {
      type: 'string',
      comment: 'The subdomain for the site',
      optional: true,
    },
    user: {
      type: UserPreview,
      comment:
        'Indicates how the row was added to the table; user ID or null (will display "Transcend Consent")',
      optional: true,
    },
    createdAt: {
      type: 'Date',
      comment:
        'The date the site was first added to the table, whether by telemetry or manually',
    },
    updatedAt: {
      type: 'Date',
      comment:
        'The date the site was last updated, whether by telemetry or manually',
      optional: true,
    },
    deletedAt: {
      type: 'Date',
      comment:
        'The date the site was deleted by the user, or reverted by telemetry',
      optional: true,
    },
    description: {
      type: 'string',
      comment: 'Optional free text notes field',
      optional: true,
    },
    organization: {
      type: Organization,
      comment: 'ID of the airgap bundle that this consent site belongs to',
    },
  },
});

/** Override type */
export type ConsentSite = SchemaToType<typeof ConsentSite>;

export const FilterConsentSiteInput = mkInput({
  name: 'FilterConsentSiteInput',
  comment: 'Input for filtering consent sites',
  fields: {
    lastDiscoveredAt: {
      type: 'Date',
      comment:
        'Indicates if telemetry data has been encountered from this site, and if so how old it is',
      optional: true,
    },
    site: {
      type: 'string',
      comment: 'The TLD for the site',
      optional: true,
    },
    host: {
      type: 'string',
      comment: 'The subdomain for the site, if it exists',
      optional: true,
    },
    userId: {
      type: 'string',
      comment:
        'Indicates how the row was added to the table; user ID or if by Telemetry data, show "Transcend Consent"',
      optional: true,
    },
    description: {
      type: 'string',
      comment: 'Optional free text notes field',
      optional: true,
    },
  },
});

/** Override type */
export type FilterConsentSiteInput = SchemaToType<
  typeof FilterConsentSiteInput
>;

export const CreateOrUpdateConsentSiteInput = mkInput({
  name: 'CreateOrUpdateConsentSiteInput',
  comment: 'Data used to create or update a consent site record',
  fields: {
    id: {
      type: 'id',
      comment: 'The ID of this site',
      modelName: 'consentSite',
      optional: true,
    },
    site: {
      type: 'string',
      comment: 'The TLD for the site',
      optional: true,
    },
    host: {
      type: 'string',
      comment: 'The subdomain for the site',
      optional: true,
    },
    userId: {
      type: 'string',
      comment:
        'Indicates how the row was added to the table; user ID or if by Telemetry data, show "Transcend Consent"',
      optional: true,
    },
    description: {
      type: 'string',
      comment: 'Optional free text notes field',
      optional: true,
    },
    airgapBundleId: {
      type: 'id',
      modelName: 'airgapBundle',
      comment: 'ID of the airgap bundle that this consent site belongs to',
    },
  },
});

/** Override type */
export type CreateOrUpdateConsentSiteInput = SchemaToType<
  typeof CreateOrUpdateConsentSiteInput
>;

export const DeleteConsentSiteInput = mkInput({
  name: 'DeleteConsentSiteInput',
  comment: 'Input for deleting consent sites by ID',
  fields: {
    id: {
      type: 'id',
      modelName: 'consentSite',
      comment: 'ID of the consent site to delete',
    },
  },
});

/** Override type */
export type DeleteConsentSiteInput = SchemaToType<
  typeof DeleteConsentSiteInput
>;
